import { AttachmentList } from './../../../inspection/shared/models/Inspection';
import { InspectionService } from './../../../inspection/shared/services/inspection.service';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { ErisFormFieldType } from 'eris-forms';
import { environment } from '../../../../environments/environment';
import { SubDataVariables } from '../../../../environments/app.variables';
import { PreviewModalService } from '../../../shared/services/preview-modal.service';
import { ValidationErrors, ValidatorFn } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-upload-list',
  templateUrl: './upload-list.component.html',
  styleUrls: ['./upload-list.component.scss']
})
export class UploadListComponent extends ErisFormFieldType implements OnInit, AfterViewInit, OnDestroy {

  public isEditMode = false;
  documentListTableModel = {
    header: [
      { label: 'Attachment', name: 'documentName', subtitle: 'documentNameSubtitle' },
      { label: 'Upload', name: 'upload', isTemplate: true },
      { label: 'Status', name: 'status', isTemplate: true },
      { label: 'Preview', name: 'preview', isTemplate: true }
    ],
    data: [],
    options: { stripped: true, bordered: false, rowNumber: true }
  };

  uploadedDocumentList: any[] = [];

  documentListApi = '';
  attachmentlist = new AttachmentList();
  requiredDocumentList: any[] = [];
  method: string;

  @ViewChild('documentUploadTemplate')
  private documentUploadTemplate: TemplateRef<any>;

  @ViewChild('documentStatusTemplate')
  private documentStatusTemplate: TemplateRef<any>;

  @ViewChild('documentPreviewTemplate')
  private documentPreviewTemplate: TemplateRef<any>;

  private readonly destroy$ = new Subject<void>();
  private attachmentListSub: Subscription | null = null;
  private dependsOnRefreshTimer: ReturnType<typeof setTimeout> | null = null;
  private attachmentListRequestId = 0;
  private attachmentListLoaded = false;

  constructor(
    private inspectionService: InspectionService,
    private cdr: ChangeDetectorRef,
    private previewModalService: PreviewModalService
  ) {
    super();
  }

  ngOnInit(): void {
    const customURL = this.field.templateOptions.extraData?.customURL;
    if (customURL) {
      this.documentListApi = customURL;
      this.getAttachmentList();
    } else {
      this.initializeForm();
    }

    this.setFormValidators();
  }

  ngAfterViewInit(): void {
    const uploadHeader = this.documentListTableModel.header.find(h => h.name === 'upload' && h.isTemplate);
    const statusHeader = this.documentListTableModel.header.find(h => h.name === 'status' && h.isTemplate);
    const previewHeader = this.documentListTableModel.header.find(h => h.name === 'preview' && h.isTemplate);

    // @ts-ignore
    if (uploadHeader) { uploadHeader.templateRef = this.documentUploadTemplate; }
    // @ts-ignore
    if (statusHeader) { statusHeader.templateRef = this.documentStatusTemplate; }
    // @ts-ignore
    if (previewHeader) { previewHeader.templateRef = this.documentPreviewTemplate; }
  }

  ngOnDestroy(): void {
    if (this.dependsOnRefreshTimer != null) {
      clearTimeout(this.dependsOnRefreshTimer);
      this.dependsOnRefreshTimer = null;
    }
    this.attachmentListSub?.unsubscribe();
    this.attachmentListSub = null;
    this.destroy$.next();
    this.destroy$.complete();
  }

  initializeForm() {
    const dependsOnControl = this.form?.parent?.get(this.field.templateOptions.dependsOnField);
    if (!dependsOnControl) {
      return;
    }

    const scheduleAttachmentListRefresh = () => {
      if (this.dependsOnRefreshTimer != null) {
        clearTimeout(this.dependsOnRefreshTimer);
      }

      // Wait for expressionProperties (urlParameter / filters) to settle.
      this.dependsOnRefreshTimer = setTimeout(() => {
        this.dependsOnRefreshTimer = null;
        const extraData = this.field.templateOptions.extraData || {};
        const parameter = extraData.urlParameter;
        const dataSource = this.field.templateOptions.dataSourceFn?.(parameter);
        if (!dataSource?.url) {
          return;
        }

        this.attachmentlist.category = extraData.categoryTypeID;
        this.attachmentlist.agency = extraData.AgencyTypeID;
        this.attachmentlist.applicantType = extraData.ApplicantTypeID;
        this.documentListApi = dataSource.url;
        this.method = dataSource.method;
        this.getAttachmentList();
      }, 500);
    };

    dependsOnControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => scheduleAttachmentListRefresh());

    // Edit mode often patches dependsOn before/without a later change event.
    if (dependsOnControl.value) {
      scheduleAttachmentListRefresh();
    }
  }

  getAttachmentList() {
    // TODO: make upload-list accept GET/POST generically like other eris-forms components
    this.isEditMode = !!this.field.templateOptions.extraData?.editing;
    if (!this.documentListApi) {
      return;
    }

    const requestId = ++this.attachmentListRequestId;
    this.attachmentListSub?.unsubscribe();
    this.attachmentListSub = this.inspectionService
      .getAttachmentData(environment.baseUrl + this.documentListApi, this.method, this.attachmentlist)
      .subscribe({
        next: (result) => {
          if (requestId !== this.attachmentListRequestId) {
            return;
          }
          this.applyAttachmentListResult(result);
        },
        error: () => {
          if (requestId !== this.attachmentListRequestId) {
            return;
          }
          this.attachmentListLoaded = true;
          this.requiredDocumentList = [];
          this.prepareAttachmentList([]);
          this.uploadedDocumentList = [];
          this.syncFormControl();
          this.cdr.detectChanges();
        }
      });
  }

  private applyAttachmentListResult(result: any) {
    const docs = Array.isArray(result) ? result : (result != null ? [result] : []);
    const newResult = [];
    this.requiredDocumentList = [];
    this.uploadedDocumentList = [];

    docs.forEach((element) => {
      if (!element) {
        return;
      }
      if (element.required) {
        this.requiredDocumentList.push(element);
      }
      newResult.push({
        active: element.active,
        createdDate: element.createdDate,
        description: element.description,
        focalPersonDocument: element.focalPersonDocument,
        id: element.id,
        modifiedDate: element.modifiedDate,
        name: element.name,
        plcDocument: element.plcDocument,
        isRequired: element.required,
        required: element.required,
        rowGuid: element.rowGuid,
      });
    });

    this.prepareAttachmentList(newResult);

    const allowedRefIds = new Set(newResult.map(r => Number(r.id)));
    const currentDocs = Array.isArray(this.field.formControl.value)
      ? this.field.formControl.value
      : [];
    const docsToRestore = currentDocs.filter(d => {
      const ref = Number(d?.referenceID ?? d?.referenceId);
      return Number.isFinite(ref) && allowedRefIds.has(ref);
    });

    docsToRestore.forEach(doc => {
      this.addFile(doc, null, false);
      if (!this.isEditMode) {
        return;
      }
      const ref = Number(doc.referenceID ?? doc.referenceId);
      const item = this.documentListTableModel.data.find(
        row => Number(row.upload.fileUploadModel.referenceId) === ref
      );
      if (item) {
        item.upload.fileUploadModel['fileName'] = this.getFileName(doc.filePath);
      }
    });

    this.attachmentListLoaded = true;
    this.syncFormControl();
    this.cdr.detectChanges();
  }

  prepareAttachmentList(moduleDocuments: any[]) {
    this.documentListTableModel.data = moduleDocuments.map(modDoc => {
      const documentInfo$ = { id: 0, title: modDoc.name, fileType: 'application/pdf' };
      return {
        documentName: modDoc.name,
        documentNameSubtitle: modDoc.description,
        referenceId: modDoc.id,
        documentInfo: documentInfo$,
        upload: {
          documentInfo: documentInfo$,
          fileUploadModel: {
            referenceId: modDoc.id,
            originalFileName: this.field.templateOptions.extraData?.originalFileName,
            tempFileName: modDoc.name
          }
        },
        status: {
          documentInfo: documentInfo$
        },
        preview: {
          documentInfo: documentInfo$
        },
        required: modDoc.required
      };
    });
  }

  previewAttachment(documentInfo: any): void {
    if (!documentInfo?.id) {
      return;
    }
    const attachmentUrl = SubDataVariables.INSPECTION_DOC_URL + '/' + documentInfo.id;
    this.previewModalService.openModal(attachmentUrl, documentInfo.name, true);
  }

  addFile(uploadedDocument: any, field: any, syncControl: boolean = true) {
    if (!uploadedDocument) {
      return;
    }

    // Upload API / form patches may use referenceID or referenceId; coerce to number.
    const referenceID = Number(
      uploadedDocument.referenceID ?? uploadedDocument.referenceId
    );
    const documentId = Number(uploadedDocument.id);
    if (!Number.isFinite(referenceID) || !Number.isFinite(documentId) || documentId <= 0) {
      return;
    }

    const document = this.documentListTableModel.data.find(
      d => Number(d.referenceId) === referenceID
    );
    if (!document) {
      return;
    }

    let response = this.field.templateOptions.responseFn
      ? this.field.templateOptions.responseFn(uploadedDocument)
      : uploadedDocument;
    if (response == null || typeof response !== 'object') {
      response = uploadedDocument;
    }

    // Always persist id + referenceID on the form value (responseFn often only console.logs).
    response = {
      ...response,
      id: documentId,
      referenceID
    };

    const existingIndex = this.uploadedDocumentList.findIndex(
      element => Number(element.referenceID ?? element.referenceId) === referenceID
    );
    if (existingIndex >= 0) {
      this.uploadedDocumentList[existingIndex] = response;
    } else {
      this.uploadedDocumentList.push(response);
    }

    document.id = documentId;
    document.status.documentInfo.isUploaded = true;
    document.preview.documentInfo.id = documentId;

    // Always keep form control in sync with uploads so Edit/Create/FIR submit
    // payloads include newly attached and replaced documents.
    if (syncControl) {
      this.syncFormControl();
      this.cdr.detectChanges();
    }
  }

  private syncFormControl() {
    if (!this.field?.formControl) {
      return;
    }
    this.field.formControl.setValue([...this.uploadedDocumentList]);
    this.field.formControl.updateValueAndValidity({ emitEvent: false });
  }

  private getFileName(filePath: string): string {
    if (!filePath || typeof filePath !== 'string') {
      return '';
    }
    return filePath.replace(/^.*[\\\/]/, '');
  }

  setFormValidators() {
    const requiredDocumentsValidator: ValidatorFn = (): ValidationErrors | null => {
      return this.validateForm() ? null : { requiredDocumentsNotUploaded: true };
    };
    this.field.formControl.setValidators(requiredDocumentsValidator);
    this.field.formControl.updateValueAndValidity({ emitEvent: false });
  }

  validateForm() {
    // Block submit until the required-document list has loaded at least once.
    if (!this.attachmentListLoaded) {
      return false;
    }
    if (this.documentListTableModel.data.length === 0) {
      return true;
    }
    return this.documentListTableModel.data
      .filter(md => md.required)
      .every(md => this.uploadedDocumentList.some(
        ud => Number(ud.referenceID ?? ud.referenceId) === Number(md.referenceId)
      ));
  }
}
